import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import { getMenuLanguages } from "../../../../../constants/menuLanguages";
import {
  IMAGE_PICKER_ACCEPT,
  validateImageFile,
} from "../../../../../utils/imageValidation";
import { getMenuDetailRawValue } from "./utils";
import WizardVisualSettings from "../WizardVisualSettings";

const ExtraPanel = ({ open, onToggle, title, meta, children }) => (
  <div className={`start-step1-extra${open ? " is-open" : ""}`}>
    <button
      type="button"
      className="start-step1-extra__toggle"
      onClick={onToggle}
      aria-expanded={open}
    >
      <span className="start-step1-extra__chevron" aria-hidden="true">
        <i className="fa fa-chevron-down" />
      </span>
      <span className="start-step1-extra__title">{title}</span>
      {meta ? <span className="start-step1-extra__meta">{meta}</span> : null}
    </button>
    {open ? <div className="start-step1-extra__body">{children}</div> : null}
  </div>
);

const Step1Menu = ({
  draft,
  setDraft,
  goNext,
  setMenuPhotoFile,
  menuPhotoPreview,
  setMenuPhotoPreview,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const languages = getMenuLanguages(t);

  const primaryLanguage = draft.menu?.primaryLanguage || "en";
  const optionalLanguages = Array.isArray(draft.menu?.optionalLanguages)
    ? draft.menu.optionalLanguages.filter((code) => code !== primaryLanguage)
    : [];

  const [languagesOpen, setLanguagesOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);

  const primaryMenuNameRaw = getMenuDetailRawValue(
    draft.menu?.menuDetails,
    "menuName",
    primaryLanguage
  );
  const primaryMenuName = primaryMenuNameRaw.trim();

  const canNext = useMemo(
    () => primaryMenuName.trim().length > 0,
    [primaryMenuName]
  );

  const primaryLangMeta = languages.find((lang) => lang.code === primaryLanguage);

  const updateMenuDetails = (language, menuName) => {
    setDraft((prev) => {
      const next = { ...prev, menu: { ...prev.menu } };
      const details = Array.isArray(next.menu.menuDetails)
        ? [...next.menu.menuDetails]
        : [];
      const idx = details.findIndex((d) => d.language === language);
      if (idx >= 0) {
        details[idx] = { ...details[idx], language, menuName };
      } else {
        details.push({ language, menuName });
      }
      next.menu.menuDetails = details;
      return next;
    });
  };

  const setPrimaryLanguage = (code) => {
    if (!code || code === primaryLanguage) return;
    setDraft((prev) => {
      const next = { ...prev, menu: { ...prev.menu } };
      const prevOptional = Array.isArray(next.menu.optionalLanguages)
        ? next.menu.optionalLanguages.filter((lang) => lang !== code)
        : [];
      if (primaryLanguage && primaryLanguage !== code && !prevOptional.includes(primaryLanguage)) {
        prevOptional.push(primaryLanguage);
      }
      next.menu.primaryLanguage = code;
      next.menu.optionalLanguages = prevOptional.filter((lang) => lang !== code);
      return next;
    });
  };

  const toggleOptionalLanguage = (code) => {
    if (code === primaryLanguage) return;
    setDraft((prev) => {
      const next = { ...prev, menu: { ...prev.menu } };
      const current = Array.isArray(next.menu.optionalLanguages)
        ? [...next.menu.optionalLanguages]
        : [];
      const exists = current.includes(code);
      next.menu.optionalLanguages = exists
        ? current.filter((lang) => lang !== code)
        : [...current, code];
      if (exists) {
        next.menu.menuDetails = (next.menu.menuDetails || []).filter(
          (d) => d.language !== code
        );
      }
      return next;
    });
  };

  const handlePhotoChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const { isValid, message } = validateImageFile(selectedFile, 1);
    if (!isValid) {
      swal(t("warning"), message, "warning");
      event.target.value = "";
      return;
    }

    if (menuPhotoPreview) {
      URL.revokeObjectURL(menuPhotoPreview);
    }

    setMenuPhotoFile(selectedFile);
    setMenuPhotoPreview(URL.createObjectURL(selectedFile));
  };

  const handleRemovePhoto = (event) => {
    event.stopPropagation();
    if (menuPhotoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(menuPhotoPreview);
    }
    setMenuPhotoFile(null);
    setMenuPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <div className="mt-3 start-step-content">
        <div className="start-step-hero">
          <h2 className="start-step-title">
            <span className="step-badge">1</span>
            {t("wizardCreateMenuHeading")}
          </h2>
          <p className="start-step-subtitle">{t("wizardCreateMenuSub")}</p>
        </div>

        <div className="start-step1">
          <section className="start-step1-card" aria-label={t("wizardCreateMenuHeading")}>
            <div className="start-step1-identity">
              <div className="start-menu-avatar-wrap">
                <button
                  type="button"
                  className={`start-menu-avatar${menuPhotoPreview ? " has-photo" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label={
                    menuPhotoPreview ? t("change") : t("wizardAddLogo")
                  }
                >
                  {menuPhotoPreview ? (
                    <img
                      src={menuPhotoPreview}
                      alt=""
                      className="start-menu-avatar__img"
                    />
                  ) : (
                    <>
                      <span className="start-menu-avatar__icon" aria-hidden="true">
                        <i className="fa fa-image" />
                      </span>
                      <span className="start-menu-avatar__label">{t("wizardAddLogo")}</span>
                      <span className="start-menu-avatar__hint">
                        {t("wizardMenuLogoFormatHint")}
                      </span>
                    </>
                  )}
                </button>
                {menuPhotoPreview ? (
                  <button
                    type="button"
                    className="start-menu-avatar__remove"
                    onClick={handleRemovePhoto}
                    aria-label={t("remove")}
                    title={t("remove")}
                  >
                    <i className="fa fa-times" aria-hidden="true" />
                  </button>
                ) : null}
                <input
                  ref={fileInputRef}
                  hidden
                  type="file"
                  accept={IMAGE_PICKER_ACCEPT}
                  onChange={handlePhotoChange}
                />
              </div>

              <div className="start-step1-identity__fields">
                <label className="start-step1-label" htmlFor="wizard-menu-title">
                  {t("wizardMenuTitleLabel")}
                  <span className="start-step1-required" aria-hidden="true">*</span>
                  {primaryLangMeta ? (
                    <span className="start-step1-label__lang">{primaryLangMeta.label}</span>
                  ) : null}
                </label>
                <input
                  id="wizard-menu-title"
                  type="text"
                  className={`form-control start-input start-step1-title-input${
                    canNext ? " is-ready" : ""
                  }`}
                  placeholder={t("placeholderMenuNameExample")}
                  value={primaryMenuNameRaw}
                  onChange={(e) => updateMenuDetails(primaryLanguage, e.target.value)}
                  autoComplete="off"
                  aria-required="true"
                />

                <p className="start-step1-label start-step1-label--spaced">
                  {t("wizardPrimaryLanguageLabel")}
                </p>
                <div
                  className="start-language-grid"
                  role="radiogroup"
                  aria-label={t("wizardPrimaryLanguageLabel")}
                >
                  {languages.map((lang) => {
                    const isSelected = lang.code === primaryLanguage;
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        className={`start-language-chip${isSelected ? " is-primary" : ""}`}
                        onClick={() => setPrimaryLanguage(lang.code)}
                      >
                        {lang.flag ? (
                          <img src={lang.flag} alt="" className="start-language-chip__flag" />
                        ) : null}
                        <span>{lang.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          <ExtraPanel
            open={languagesOpen}
            onToggle={() => setLanguagesOpen((v) => !v)}
            title={t("wizardOptionalLanguagesLabel")}
            meta={
              optionalLanguages.length > 0
                ? t("wizardMoreLanguagesCount", { count: optionalLanguages.length })
                : t("optional")
            }
          >
            <p className="start-step1-extra__hint">{t("wizardOptionalLanguagesHint")}</p>
            <div className="start-language-grid start-language-grid--optional">
              {languages
                .filter((lang) => lang.code !== primaryLanguage)
                .map((lang) => {
                  const isSelected = optionalLanguages.includes(lang.code);
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      className={`start-language-chip start-language-chip--optional${
                        isSelected ? " is-selected" : ""
                      }`}
                      onClick={() => toggleOptionalLanguage(lang.code)}
                    >
                      {lang.flag ? (
                        <img src={lang.flag} alt="" className="start-language-chip__flag" />
                      ) : null}
                      <span>{lang.label}</span>
                    </button>
                  );
                })}
            </div>

            {optionalLanguages.length > 0 && (
              <div className="start-optional-titles">
                {optionalLanguages.map((code) => {
                  const lang = languages.find((item) => item.code === code);
                  if (!lang) return null;
                  return (
                    <div key={code} className="start-optional-title-row">
                      <label className="start-step1-label" htmlFor={`wizard-menu-title-${code}`}>
                        {t("wizardMenuTitleLabel")}
                        <span className="start-step1-label__lang">{lang.label}</span>
                      </label>
                      <input
                        id={`wizard-menu-title-${code}`}
                        type="text"
                        className="form-control start-input start-input--compact"
                        placeholder={t("placeholderMenuNameExample")}
                        value={getMenuDetailRawValue(draft.menu?.menuDetails, "menuName", code)}
                        onChange={(e) => updateMenuDetails(code, e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </ExtraPanel>

          <ExtraPanel
            open={appearanceOpen}
            onToggle={() => setAppearanceOpen((v) => !v)}
            title={t("wizardVisualPresetLabel")}
            meta={
              draft?.visualSettingsMode === "custom"
                ? t("wizardVisualPresetCustom")
                : t("wizardVisualPresetRecommended")
            }
          >
            <WizardVisualSettings draft={draft} setDraft={setDraft} hideIntro />
          </ExtraPanel>
        </div>
      </div>

      <div className="toolbar toolbar-bottom start-toolbar">
        <div className="wizard-step-counter">{t("wizardStep1of9")}</div>
        <button
          className={`btn btn-primary sw-btn-next start-btn-next ${!canNext ? "disabled" : ""}`}
          disabled={!canNext}
          onClick={goNext}
        >
          {t("wizardContinue")} <span aria-hidden="true">→</span>
        </button>
      </div>
    </>
  );
};

export default Step1Menu;
